import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';

const API_BASE = import.meta.env['VITE_API_BASE_URL'] ?? '/api/v1';

// Named export used by AuthContext and all modules
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Legacy alias — prefer `api`
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

let accessToken: string | null = null;
let refreshPromise: Promise<string> | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

// Single-flight refresh: concurrent 401s share ONE /auth/refresh call. Refresh tokens are
// single-use (rotated server-side), so two parallel refreshes would spend the same token and
// 401 the loser — which previously hard-redirected and bounced you off the page.
const refreshAccessToken = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post<{ data: { accessToken: string } }>(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
      .then((res) => {
        const newToken = res.data.data.accessToken;
        setAccessToken(newToken);
        return newToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Wire both instances with the same interceptors
for (const instance of [api, apiClient]) {
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Never attempt a token refresh for the auth endpoints themselves.
      // Refreshing on a failed /auth/refresh (or /auth/login) causes an
      // infinite reload loop when there is no valid session.
      const url = original?.url ?? '';
      const isAuthEndpoint = url.includes('/auth/refresh') || url.includes('/auth/login');

      if (error.response?.status === 401 && !isAuthEndpoint && !original._retry) {
        original._retry = true;
        try {
          const newToken = await refreshAccessToken();
          original.headers.Authorization = `Bearer ${newToken}`;
          return instance(original);
        } catch {
          setAccessToken(null);
          window.location.href = '/login';
        }
      }

      return Promise.reject(error);
    },
  );
}
